import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase, getProfile } from '../lib/supabase';
import { Database } from '../types/database';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  user: SupabaseUser | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // Check if Supabase client is available
      if (!supabase) {
        console.log('Supabase not configured - skipping auth initialization');
        if (mounted) {
          setLoading(false);
        }
        return;
      }

      try {
        console.log('Initializing auth...');

        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error);
        }

        console.log('Initial session:', session?.user?.id ? 'Found user' : 'No user');

        if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user) {
            loadProfile(session.user.id);
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes if Supabase is available
    const subscription = supabase?.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        console.log('Auth state changed:', event, session?.user?.id ? 'User found' : 'No user');
        setUser(session?.user ?? null);

        if (session?.user) {
          loadProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.data.subscription.unsubscribe();
    };
  }, []);

  const loadProfile = async (userId: string) => {
    if (!supabase) return;

    try {
      console.log('Loading profile for user:', userId);
      const { data, error } = await getProfile(userId);

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading profile:', error);
      }

      if (data) {
        console.log('Profile loaded:', data.role);
        setProfile(data);
      } else {
        console.log('No profile found');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, userData: any) => {
    if (!supabase) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    console.log('Signing up:', email);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          data: {
            full_name: userData.full_name?.trim() || '',
            role: userData.role || 'donor'
          }
        }
      });

      if (error) {
        console.error('SignUp error:', error);
        return { data, error };
      }

      console.log('SignUp success:', data.user?.id ? 'User created' : 'Confirmation required');
      return { data, error: null };
    } catch (err: any) {
      console.error('SignUp exception:', err);
      const message = err?.message?.includes('fetch')
        ? 'Unable to reach the server. Please check your connection and try again.'
        : (err?.message || 'Sign up failed. Please try again.');
      return { data: null, error: { message } };
    }
  };

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    console.log('Signing in:', email);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) {
        console.error('SignIn error:', error);
        return { data, error };
      }

      console.log('SignIn success:', data.user?.id ? 'User authenticated' : 'No user');
      return { data, error: null };
    } catch (err: any) {
      console.error('SignIn exception:', err);
      const message = err?.message?.includes('fetch')
        ? 'Unable to reach the server. Please check your connection and try again.'
        : (err?.message || 'Sign in failed. Please try again.');
      return { data: null, error: { message } };
    }
  };

  const signOut = async () => {
    if (!supabase) return;

    console.log('Signing out...');
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('SignOut error:', error);
      throw error;
    }
    console.log('SignOut success');
    setProfile(null);
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error('No user logged in');
    if (!supabase) throw new Error('Supabase not configured');

    console.log('Updating profile for user:', user.id);
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', user.id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Profile update error:', error);
      throw error;
    }

    console.log('Profile updated successfully');
    setProfile(data);
    return { data, error: null };
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
